import { useState, useEffect, useCallback } from 'react';

// Debug helper function
const debugLog = (category, message, data = null) => {
  const timestamp = new Date().toISOString();
  console.group(`🐛 [${timestamp}] ${category}`);
  console.log(message);
  if (data) {
    console.log('Data:', data);
  }
  console.groupEnd();
};

const useVapi = () => {
  const [vapi, setVapi] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [callId, setCallId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isMicrophoneOn, setIsMicrophoneOn] = useState(true);

  // Get API key from environment variables
  const apiKey = import.meta.env.VITE_VAPI_API_KEY;

  // Debug state changes
  useEffect(() => {
    debugLog('STATE_CHANGE', 'Hook state updated', {
      isConnected,
      isLoading,
      error,
      transcript,
      isSpeaking,
      callId,
      hasVapi: !!vapi,
      hasApiKey: !!apiKey,
      apiKeySource: 'environment'
    });
  }, [isConnected, isLoading, error, transcript, isSpeaking, callId, vapi, apiKey]);

  useEffect(() => {
    const initVapi = async () => {
      if (!apiKey) {
        debugLog('INIT', 'No API key provided, skipping Vapi initialization');
        return;
      }
      
      try {
        debugLog('INIT', 'Starting Vapi initialization', { apiKey: apiKey.substring(0, 10) + '...' });
        const { default: Vapi } = await import('@vapi-ai/web');
        const vapiInstance = new Vapi(apiKey);
        
        // Set up event listeners with enhanced debugging
        vapiInstance.on('call-start', (data) => {
          debugLog('VAPI_EVENT', 'Call started', { ...data, callId });
          setIsConnected(true);
          setIsLoading(false);
          setIsMuted(false);
          setIsMicrophoneOn(true);
        });

        vapiInstance.on('call-end', (data) => {
          debugLog('VAPI_EVENT', 'Call ended', { ...data, callId });
          setIsConnected(false);
          setIsLoading(false);
          setIsSpeaking(false);
          setIsMuted(false);
          setIsMicrophoneOn(true);
          // Clear Call ID when call ends
          setCallId(null);
          debugLog('CALL_ID', 'Call ID cleared on call end');
        });

        vapiInstance.on('speech-start', (data) => {
          debugLog('VAPI_EVENT', 'Speech started', data);
          setIsSpeaking(true);
        });

        vapiInstance.on('speech-end', (data) => {
          debugLog('VAPI_EVENT', 'Speech ended', data);
          setIsSpeaking(false);
        });

        vapiInstance.on('message', (message) => {
          debugLog('VAPI_EVENT', 'Message received', message);
          if (message.type === 'transcript' && message.transcript) {
            debugLog('TRANSCRIPT', 'Setting new transcript', { transcript: message.transcript });
            setTranscript(message.transcript);
          } else if (message.type === 'function-call') {
            addMessage('system', `Function called: ${message.functionCall.name}`);
            if (message.functionCall.name == 'timer_tool') {
              //vapiInstance.say('Let me set that timer for you.', false);
            }
          } else if (message.type === 'tool-calls') {
            debugLog('TOOL-CALLS', 'Received New Tool Call', { toolCalls: message.toolCalls[0] });
            //vapiInstance.say('Let me set that timer for you.', false);

            // Check if toolCalls array exists and has items
            if (message.toolCalls && message.toolCalls.length > 0) {
              // Get the first tool call's name
              const toolCall = message.toolCalls[0];
              const toolName = toolCall.function.name;
              const toolArguments = toolCall.function.arguments;
              debugLog('DEBUG_MESSAGE', `toolCall: ${toolCall}, toolName: ${toolName}`) 
              // Execute code based on the tool name
              if (toolName === 'timer_tool') {
                addMessage('system', `Function called: ${toolName}`);

                // Extract the Timer value
                const timerSeconds = parseInt(toolArguments.Timer);
                
                console.log(`Timer tool called for ${timerSeconds} seconds`);
                
                // Set your timer here
                setTimeout(() => {
                  console.log('Timer finished!');
                  vapiInstance.say('Your timer just went off. Do you want me to set another one?', false);
                  // Your timer completion code here
                }, timerSeconds * 1000); // Convert seconds to milliseconds
                debugLog(`Timer set for ${timerSeconds} seconds`, false);
              }
              
 
            }


         } 
        });

        vapiInstance.on('error', (error) => {
          debugLog('VAPI_ERROR', 'Vapi error occurred', error);
          setError(error.message || 'An error occurred');
          setIsLoading(false);
          setIsMuted(false);
          setIsMicrophoneOn(true);
        });

        // Add additional event listeners for debugging
        vapiInstance.on('volume-level', (volume) => {
          //debugLog('VAPI_EVENT', 'Volume level', { volume });
        });

        vapiInstance.on('function-call', (functionCall) => {
          debugLog('VAPI_EVENT', 'Function call', functionCall);
        });

        setVapi(vapiInstance);
        debugLog('INIT', 'Vapi initialization completed successfully');
      } catch (err) {
        debugLog('INIT_ERROR', 'Failed to initialize Vapi', err);
        setError('Failed to initialize voice chat');
      }
    };

    initVapi();
  }, [apiKey]);

  const startCall = useCallback(async (assistantIdOrConfig) => {
    if (!vapi) {
      debugLog('START_CALL_ERROR', 'No Vapi instance available');
      return;
    }
    
    try {
      // Determine if we're using assistant ID or full config
      const isAssistantId = typeof assistantIdOrConfig === 'string';
      
      debugLog('START_CALL', isAssistantId ? 'Starting call with assistant ID' : 'Starting call with assistant config', {
        type: isAssistantId ? 'assistantId' : 'config',
        value: isAssistantId ? assistantIdOrConfig : assistantIdOrConfig
      });
      
      setIsLoading(true);
      setError(null);
      
      let callObject;
      if (isAssistantId) {
        // Call with assistant ID
        callObject = await vapi.start(assistantIdOrConfig);
      } else {
        // Call with full assistant configuration (legacy support)
        callObject = await vapi.start(assistantIdOrConfig);
      }
      
      // Capture Call ID from the returned call object
      if (callObject && callObject.id) {
        setCallId(callObject.id);
        debugLog('CALL_ID', 'Call ID captured and stored', {
          callId: callObject.id,
          callObject: callObject
        });
      } else {
        debugLog('CALL_ID', 'Warning: No Call ID found in call object', { callObject });
      }
      
      debugLog('START_CALL', 'Call start request sent successfully', {
        callId: callObject?.id,
        hasCallObject: !!callObject
      });
    } catch (err) {
      debugLog('START_CALL_ERROR', 'Failed to start call', err);
      setError('Failed to start voice chat');
      setIsLoading(false);
      // Clear any partial Call ID on error
      setCallId(null);
    }
  }, [vapi]);

  const endCall = useCallback(async () => {
    if (!vapi) {
      debugLog('END_CALL_ERROR', 'No Vapi instance available');
      return;
    }
    
    try {
      debugLog('END_CALL', 'Ending call', { callId });
      await vapi.stop();
      debugLog('END_CALL', 'Call end request sent successfully', { callId });
    } catch (err) {
      debugLog('END_CALL_ERROR', 'Failed to end call', { callId, error: err });
      setError('Failed to end voice chat');
    }
  }, [vapi, callId]);

  const addMessage = useCallback((type, content) => {
    setMessages(prev => [...prev, {
      time: new Date().toLocaleTimeString(),
      type,
      content,
    }]);
  }, []);

  const sendMessage = useCallback(() => {
    if (!vapi) return;
    
    // DEBUG: Log variable availability
    debugLog('SEND_MESSAGE_DEBUG', 'Variable check', {
      hasVapi: !!vapi,
      vapiType: typeof vapi,
      hasVapiInstance: typeof vapiInstance !== 'undefined'
    });
    
    // Example of sending a background message to the assistant
    vapi.send({
      type: "add-message",
      message: {
        role: "system",
        content: "The user has indicated they want you to cluck like a chicken."
      }
    });
    addMessage('system', 'Background message sent to assistant');
  }, [vapi, addMessage]);

  const handleManualSay = useCallback((text, endCallAfter = false) => {
    if (!vapi || !text.trim()) return;

    console.log('handleManualSay called, vapi is:', vapi);
    
    // DEBUG: Log variable availability
    debugLog('HANDLE_MANUAL_SAY_DEBUG', 'Variable check', {
      hasVapi: !!vapi,
      vapiType: typeof vapi,
      hasVapiInstance: typeof vapiInstance !== 'undefined'
    });

    try {
      const interruptionsEnabled = true;
      const interruptAssistantEnabled = true;
      // Use the full say() method signature with all 4 parameters
      vapi.say(text, endCallAfter, interruptionsEnabled, interruptAssistantEnabled);

      const statusParts = [
        `Manual say: "${text}"`,
        endCallAfter ? 'end call after' : null,
        `interrupt user: ${interruptionsEnabled ? 'enabled' : 'disabled'}`,
        `interrupt assistant: ${interruptAssistantEnabled ? 'enabled' : 'disabled'}`
      ].filter(Boolean);

      addMessage('system', statusParts.join(' | '));
    } catch (error) {
      debugLog('SAY ERROR', 'Error with manual say:', { callId, error: error });
      addMessage('system', `Error with manual say: ${error}`);
    }
  }, [vapi, callId, addMessage]);

  const toggleMute = useCallback(() => {
    if (!vapi) return;

    const newMutedState = !isMuted;
    vapi.setMuted(newMutedState);
    setIsMuted(newMutedState);
    debugLog('MUTE_TOGGLE', 'Mute state toggled', { isMuted: newMutedState });
  }, [vapi, isMuted]);

  const setMuted = useCallback((muteState) => {
    if (!vapi) return;

    vapi.setMuted(muteState);
    setIsMuted(muteState);
    debugLog('MUTE_SET', 'Mute state explicitly set', { isMuted: muteState });
  }, [vapi]);

  return {
    vapi,
    isConnected,
    isLoading,
    error,
    transcript,
    isSpeaking,
    callId,
    startCall,
    endCall,
    messages,
    sendMessage,
    handleManualSay,
    isMuted,
    toggleMute,
    setMuted,
    isMicrophoneOn
  };
};

export default useVapi;